import { Link } from "react-router-dom";
import { Check, Crown, ArrowRight } from "lucide-react";
import SeoHead from "../components/SeoHead";
import { usePricing } from "../lib/usePricing";
import { LIVE_PRO_FEATURES } from "../lib/proFeatures";
import { useAuth } from "../lib/auth";

// /pricing — one free tier + one paid plan (Pro). Price is read live from
// Stripe (usePricing); founding members get Pro free for 12 months.
const FREE_FEATURES = [
  "Unlimited single-product checks — free, no signup",
  "Plain-English verdict with the science behind it",
  "Every verdict linked to its FDA or published source",
  "Browse 1,500+ products and ingredients",
];

export default function Pricing() {
  const pricing = usePricing();
  const { user } = useAuth();
  const isPro = !!user?.is_pro;

  return (
    <main data-testid="pricing-page" className="bg-cream-50 min-h-[80vh]">
      <SeoHead
        title="BlendSafe Pricing — free skincare safety checks + Pro"
        description="BlendSafe is free for single checks. Pro checks your whole routine, builds a dermatologist discussion report, and shows what to use instead."
        canonicalPath="/pricing"
      />

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <p className="text-[10.5px] uppercase tracking-[0.28em] text-ink-mute font-semibold">
          Pricing
        </p>
        <h1
          data-testid="pricing-headline"
          className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.025em] text-ink mt-2.5 leading-[1.08]"
        >
          Free to check. Pro to manage a prescription.
        </h1>
        <p className="text-[14.5px] sm:text-[15.5px] text-ink-soft/85 mt-5 leading-relaxed max-w-xl">
          Anyone can check a single product for free. Pro is for people on a
          medication who want their whole routine checked, a report for their
          prescriber, and safer products to use instead.
        </p>

        <div className="mt-10 grid md:grid-cols-2 gap-3.5">
          {/* Free */}
          <article
            data-testid="pricing-tier-free"
            className="rounded-2xl bg-white border border-surface-line p-6 sm:p-7"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink-mute">Free</p>
                <h2 className="mt-1 text-[24px] font-semibold text-ink">Single check</h2>
              </div>
              <span className="text-[24px] font-semibold text-ink tabular-nums">$0</span>
            </div>
            <p className="mt-3 text-[13.5px] text-ink-soft leading-relaxed">
              Everything you need to know about one product and your meds.
              No signup, no card, no wall before the result.
            </p>
            <ul className="mt-5 space-y-2.5">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-[13.5px] text-ink-soft">
                  <Check className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/check"
              data-testid="pricing-cta-free"
              className="mt-6 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full border border-surface-line bg-cream-50 text-ink text-[14px] font-semibold transition-all hover:bg-cream-100"
            >
              Start free
              <ArrowRight className="w-4 h-4" />
            </Link>
          </article>

          {/* Pro */}
          <article
            data-testid="pricing-tier-pro"
            className="relative rounded-2xl bg-white border-2 border-[#7a9484] p-6 sm:p-7"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink-mute">Pro</p>
                <h2 className="mt-1 text-[24px] font-semibold text-ink flex items-center gap-2">
                  <Crown className="w-5 h-5 text-[#b08a4a]" />
                  BlendSafe Pro
                </h2>
              </div>
              <div className="text-right">
                <p data-testid="pricing-pro-price" className="text-[24px] font-semibold text-ink tabular-nums">
                  {pricing.formatted}
                </p>
                <p className="text-[11px] text-ink-mute leading-none">
                  per {pricing.interval} · cancel anytime
                </p>
              </div>
            </div>
            {isPro ? (
              <p data-testid="pricing-pro-active" className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-bold text-safe-deep">
                <Crown className="w-3.5 h-3.5" strokeWidth={2.3} />
                You're on BlendSafe Pro.
              </p>
            ) : (
              <p data-testid="pricing-founding" className="mt-3 text-[13px] font-medium" style={{ color: "#a87a32" }}>
                Founding members: Pro free for 12 months.
              </p>
            )}
            <ul className="mt-5 space-y-2.5">
              {LIVE_PRO_FEATURES.map((f) => (
                <li key={f.key} className="flex items-start gap-2.5 text-[13.5px] text-ink">
                  <Check className="w-4 h-4 text-[#7a9484] mt-0.5 shrink-0" />
                  <span><b>{f.title}</b> — {f.blurb}</span>
                </li>
              ))}
            </ul>
            <Link
              to={isPro ? "/account" : "/pro"}
              data-testid="pricing-cta-pro"
              className="mt-6 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-bold transition-all hover:scale-[1.01] active:scale-[0.99]"
            >
              {isPro ? "Manage your subscription" : "See what Pro gets you"}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </article>
        </div>
      </div>
    </main>
  );
}
